/**
 * CR314: Asset Upload Dropzone
 * 
 * Drag & drop zone + file picker for uploading images to Asset Studio.
 * Shows preview thumbnails, progress, and validation errors.
 */

import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Upload,
  X,
  CheckCircle2,
  AlertCircle,
  ImagePlus,
} from "lucide-react";
import type { AssetType } from "@/lib/asset-studio/types";
import { ASSET_TYPE_LABELS } from "@/lib/asset-studio/types";
import {
  ACCEPTED_FILE_TYPES,
  ACCEPTED_TYPES_LABEL,
  validateUploadFile,
  type UploadingFile,
} from "@/hooks/useAssetUpload";
import { MAX_UPLOAD_SIZE_MB } from "@/lib/asset-studio/assetStorage";

// ================================================================
// TYPES
// ================================================================

interface AssetUploadDropzoneProps {
  /** Called when files are selected/dropped and ready to upload */
  onUpload: (files: File[], overrides?: { type?: AssetType; tags?: string[] }) => void;
  /** Whether an upload is in progress */
  uploading?: boolean;
  /** Current upload progress items */
  uploadingFiles?: UploadingFile[];
  /** Allow multiple files */
  multiple?: boolean;
  /** Pre-selected asset type */
  defaultType?: AssetType;
  /** Compact mode (smaller dropzone) */
  compact?: boolean;
}

// ================================================================
// COMPONENT
// ================================================================

export function AssetUploadDropzone({
  onUpload,
  uploading = false,
  uploadingFiles = [],
  multiple = true,
  defaultType = "icon",
  compact = false,
}: AssetUploadDropzoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedType, setSelectedType] = useState<AssetType>(defaultType);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;

      const files = Array.from(fileList);
      const errors: string[] = [];
      const validFiles: File[] = [];

      for (const file of files) {
        const validation = validateUploadFile(file);
        if (validation.valid) {
          validFiles.push(file);
        } else {
          errors.push(`${file.name}: ${validation.error}`);
        }
      }

      setValidationErrors(errors);

      if (validFiles.length > 0) {
        onUpload(validFiles, { type: selectedType });
      }
    },
    [onUpload, selectedType]
  );

  // Drag handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      // Reset input so same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [handleFiles]
  );

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const hasActiveUploads = uploadingFiles.some(
    (f) => f.status === "uploading" || f.status === "pending"
  );

  return (
    <div className="space-y-3">
      {/* Type selector */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          Upload as:
        </span>
        <Select
          value={selectedType}
          onValueChange={(v) => setSelectedType(v as AssetType)}
          disabled={uploading}
        >
          <SelectTrigger className="w-[180px] h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ASSET_TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Dropzone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        className={`
          relative border-2 border-dashed rounded-lg cursor-pointer
          transition-colors
          ${compact ? "p-4" : "p-8"}
          ${
            isDragOver
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30"
          }
          ${uploading ? "pointer-events-none opacity-60" : ""}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          multiple={multiple}
          onChange={handleFileInput}
          className="hidden"
        />

        <div className="flex flex-col items-center gap-2 text-center">
          {isDragOver ? (
            <>
              <Upload className="h-8 w-8 text-primary animate-bounce" />
              <p className="text-sm font-medium text-primary">
                Drop files here
              </p>
            </>
          ) : (
            <>
              <ImagePlus className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">
                  Drop images here or click to browse
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {ACCEPTED_TYPES_LABEL} · Max {MAX_UPLOAD_SIZE_MB}MB
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="space-y-1">
          {validationErrors.map((err, i) => (
            <div
              key={i}
              className="flex items-start gap-2 text-xs text-destructive"
            >
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={() => setValidationErrors([])}
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Upload progress items */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          {uploadingFiles.map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-2 rounded-md bg-muted/30 border"
            >
              {/* Thumbnail */}
              <img
                src={item.preview}
                alt={item.name}
                className="w-10 h-10 rounded object-cover bg-muted"
              />

              {/* Info + progress */}
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium truncate">
                    {item.name}
                  </span>
                  {item.status === "success" && (
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                  )}
                  {item.status === "error" && (
                    <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
                  )}
                </div>

                {(item.status === "uploading" || item.status === "pending") && (
                  <Progress value={item.progress} className="h-1.5" />
                )}

                {item.status === "error" && item.error && (
                  <p className="text-[10px] text-destructive truncate">
                    {item.error}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
